import { sendAssistantTurn } from "./api.js";

// Vueltas de tools por mensaje del usuario. Una lista de códigos es una sola
// búsqueda; más de unas pocas vueltas es Claude dando vueltas sin avanzar.
const MAX_TOOL_ROUNDS = 8;

const toolResult = (toolUseId, content, isError = false) => ({
  type: "tool_result",
  tool_use_id: toolUseId,
  content,
  ...(isError ? { is_error: true } : {})
});

// Congelado para que Vue no lo observe: es grande (bloques de pensamiento) y
// se reenvía sin cambios, nunca se edita.
const frozen = message => Object.freeze(message);

/**
 * Un mensaje del usuario: llama a Claude, ejecuta las tools que pida, le
 * devuelve los resultados y repite hasta que conteste sin pedir más.
 *
 * `messages` es el historial: sólo se le agregan mensajes, nunca se editan
 * (Claude rechaza un historial con bloques de pensamiento cambiados).
 * Cada tool es { definition, run(input, ctx) } y run devuelve
 * { content, card? }: `content` es lo que lee Claude y `card` lo que se le
 * pinta al usuario, que llega por onCard con el id del tool_use.
 *
 * Devuelve el stop_reason final (end_turn, refusal, max_tokens, max_rounds).
 */
export async function runAssistantTurn({
  system,
  tools,
  messages,
  ctx,
  onCard = () => {}
}) {
  const definitions = tools.map(tool => tool.definition);
  const toolsByName = tools.reduce((acc, tool) => {
    acc[tool.definition.name] = tool;
    return acc;
  }, {});

  for (let round = 0; round < MAX_TOOL_ROUNDS; round++) {
    const response = await sendAssistantTurn({
      system,
      messages,
      tools: definitions
    });
    messages.push(frozen({ role: "assistant", content: response.content }));

    const toolUses = response.content.filter(
      block => block.type === "tool_use"
    );
    if (!toolUses.length) return response.stop_reason;

    // Cortado por max_tokens el input puede venir incompleto: no se ejecuta,
    // pero cada tool_use necesita su resultado o el siguiente turno falla.
    if (response.stop_reason !== "tool_use") {
      messages.push(
        frozen({
          role: "user",
          content: toolUses.map(block =>
            toolResult(block.id, "No se ejecutó: la respuesta se cortó.", true)
          )
        })
      );
      return response.stop_reason;
    }

    // Todos los resultados en un solo mensaje, en paralelo
    const results = await Promise.all(
      toolUses.map(async block => {
        const tool = toolsByName[block.name];
        if (!tool)
          return toolResult(block.id, `Tool desconocida: ${block.name}`, true);
        try {
          const { content, card } = await tool.run(block.input, ctx);
          if (card) onCard(block.id, card);
          return toolResult(block.id, content);
        } catch (e) {
          const message =
            e?.response?.data?.message ||
            e?.response?.data?.error ||
            e?.message ||
            "Error";
          return toolResult(block.id, `Error: ${message}`, true);
        }
      })
    );
    messages.push(frozen({ role: "user", content: results }));
  }
  return "max_rounds";
}
